import { apiService } from './ApiService';
import { AxiosResponse } from 'axios';

export interface B2BCustomer {
  id: number;
  organization_id: number;
  name: string;
  alias: string | null;
  tax_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  notes: string | null;
  delivery_days_override: number | null;
  is_active: boolean;
  portal_enabled: boolean;
  portal_must_change_password: boolean;
  portal_last_login_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface B2BCustomerCreate {
  name: string;
  alias?: string | null;
  tax_id?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  country?: string | null;
  city?: string | null;
  address?: string | null;
  notes?: string | null;
  delivery_days_override?: number | null;
}

export type B2BCustomerUpdate = Partial<B2BCustomerCreate> & { is_active?: boolean };

export interface B2BApiToken {
  id: number;
  name: string;
  client_id: string;
  scopes: string[];
  b2b_customer_id: number;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

export interface B2BApiTokenCreated extends B2BApiToken {
  client_secret: string;
  message: string;
}

export interface B2BApiTokenListResponse {
  tokens: B2BApiToken[];
  total: number;
}

export interface B2BCustomerListResponse {
  customers: B2BCustomer[];
  total: number;
}

const BASE = '/api/v1/crm/customers';

class B2BCustomerService {
  list(params?: {
    q?: string;
    page?: number;
    limit?: number;
    include_inactive?: boolean;
  }): Promise<AxiosResponse<B2BCustomerListResponse>> {
    return apiService.get<B2BCustomerListResponse>(BASE + '/', { params });
  }

  get(id: number): Promise<AxiosResponse<B2BCustomer>> {
    return apiService.get<B2BCustomer>(`${BASE}/${id}`);
  }

  create(data: B2BCustomerCreate): Promise<AxiosResponse<B2BCustomer>> {
    return apiService.post<B2BCustomer>(BASE + '/', data);
  }

  update(id: number, data: B2BCustomerUpdate): Promise<AxiosResponse<B2BCustomer>> {
    return apiService.patch<B2BCustomer>(`${BASE}/${id}`, data);
  }

  remove(id: number): Promise<AxiosResponse<void>> {
    return apiService.delete<void>(`${BASE}/${id}`);
  }

  listTokens(id: number): Promise<AxiosResponse<B2BApiTokenListResponse>> {
    return apiService.get<B2BApiTokenListResponse>(`${BASE}/${id}/api-tokens`);
  }

  createToken(id: number, data: { name: string; scopes?: string[] }): Promise<AxiosResponse<B2BApiTokenCreated>> {
    return apiService.post<B2BApiTokenCreated>(`${BASE}/${id}/api-tokens`, data);
  }

  revokeToken(customerId: number, tokenId: number): Promise<AxiosResponse<void>> {
    return apiService.delete<void>(`${BASE}/${customerId}/api-tokens/${tokenId}`);
  }
}

export const b2bCustomerService = new B2BCustomerService();
